import React, { useEffect, useState } from "react";
import { Info, Search, Plus, Trash2 } from "lucide-react";
import SettingsIcon from "../../../../../assets/images/icons/settings.svg";
import { Tooltip } from "../../../../../components/Tooltip";
import Dialog from "../../../../../components/Dialogbox";
import { useDispatch, useSelector } from "react-redux";
import MusicLoader from "../../../../../helper/MusicLoader";
import MultitrackIcon from '../../../../../assets/images/icons/multitrack.svg';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { searchMastersLibrary, uploadFileFromUrls } from "../../../../../redux/actions/audioEngineer";
import { PricingPlanModal } from "../../../../../components/PricingPlanModal";
import { showToast } from "../../../../../helper/ShowToast";
import Settings from "../../AudioEngineer/sections/Settings";
import { StemTrackTable } from "./StemTrackTable";
import { useDeleteStem, useRetreiveStemsByTrack, useSearchStemsLibrary } from "../../../../../queries/urstudio/mixenhancer.query";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useNavigate } from "react-router-dom";

function convertToMB(fileSize) {
  const MB = fileSize / (1024 * 1024);
  return MB.toFixed(2); // Returns with 2 decimal places
}

const StemSplitsLibrary = ({ handleSettings, trackId, data }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [showPricingPlanModal, setShowPricingPlanModal] = React.useState(false);
  const [selectedTracks, setSelectedTracks] = React.useState([]);
  const localGenre = localStorage.getItem('genre')
  const localLoudness = localStorage.getItem('loudness')
  const localSampleRate = localStorage.getItem('sampleRate')
  const [genre, setGenre] = React.useState(localGenre || "ROCK_INDIE");
  const [loudness, setLoudness] = React.useState(localLoudness || "MEDIUM");
  const [sampleRate, setSampleRate] = React.useState(localSampleRate || "48000")
  const [isMoving, setIsMoving] = useState(false)
  const [query, setQuery] = React.useState("");
  const { hasActiveSubscription } = useSelector((state) => state.billing);
  const { data: stemsData, isLoading: stemsLoading, isError: stemsError } = useRetreiveStemsByTrack(trackId);
  const stems = stemsData?.stems?.length === 0
    ? Array.isArray(data)
      ? data
      : Object.values(data || {})
    : stemsData?.stems || [];
  const totalStorage = stems.reduce((acc, b) => acc + (b.fileSize || 0), 0);

  const debouncedQuery = useDebounce(query, 500);
  const {
    data: searchResults,
    isLoading: isSearchLoading,
  } = useSearchStemsLibrary({ id: stems?.[0]?.uploadId, q: debouncedQuery })

  const handleDialog = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  const saveSettings = () => {
    if (!hasActiveSubscription) {
      showToast("info", "Please subscribe to change settings.");
      setIsDialogOpen(!isDialogOpen);
      setShowPricingPlanModal(true);   // Open subscription modal
      return;
    } else {
      setIsDialogOpen(!isDialogOpen);
      localStorage.setItem("genre", genre)
      localStorage.setItem("loudness", loudness)
      localStorage.setItem("sampleRate", sampleRate)
      handleSettings({ genre: genre, loudness: loudness })
    }
  }

  const handleMoveToMultitrack = () => {
    if (!selectedTracks || selectedTracks.length === 0) return;
    // show moving state immediately so button shows "Moving to Multitrack..."
    setIsMoving(true);
    // find selected track objects from current tracks array
    const itemsToMove = tracks.filter(t => selectedTracks.includes(t._id));
    if (itemsToMove.length === 0) {
      // try matching by id field differently
      const alt = tracks.filter(t => selectedTracks.includes(String(t.id)));
      if (alt.length > 0) itemsToMove.push(...alt);
    }

    // map to uploadedData expected shape
    const mapped = itemsToMove.map(item => ({
      _id: item._id || item.id,
      filename: item.filename,
      originalUrl: item.stemUrl
    }));

    // merge with existing uploadedData
    let merged = [];
    mapped.forEach(m => {
      if (!merged.some(x => String(x._id) === String(m._id))) merged.push(m);
    });

    let requestBody;
    const urls = mapped.map(p => p.originalUrl).filter(Boolean);
    requestBody = {
      sourceUrls: urls
    };
    dispatch(uploadFileFromUrls(requestBody, setIsMoving, 'mixing-engineer', navigate))
  };

  const handleSelectAll = () => {
    const libArr = Array.isArray(stems) ? stems : (stems || []);
    const searchArr = Array.isArray(searchResults) ? searchResults : (searchResults?.data || []);
    const current = debouncedQuery ? searchArr : libArr;
    const tracksArray = current || [];
    if (selectedTracks.length === tracksArray.length) {
      // Deselect all
      setSelectedTracks([]);
    } else {
      // Select all - use same logic as table (track._id || idx)
      const allTrackIds = tracksArray.map((track, idx) => track._id || idx);
      setSelectedTracks(allTrackIds);
    }
  };

  const deleteMutation = useDeleteStem()
  const { isLoading } = deleteMutation

  const handleBulkDelete = () => {
    if (!selectedTracks || selectedTracks.length === 0) return;
    // Pass array for multiple IDs, single ID for single item
    const ids = selectedTracks.length === 1 ? selectedTracks[0] : selectedTracks;
    deleteMutation.mutate(ids, {
      onSuccess: () => {
        setSelectedTracks([]);
      }
    });
  };

  const searchArr = Array.isArray(searchResults) ? searchResults : (searchResults?.data || []);
  const tracks = debouncedQuery ? searchArr : stems;
  const isAllSelected = tracks.length > 0 && selectedTracks.length === tracks.length;

  return (
    <div className="bg-theme_clr1 rounded-2xl p-6 overflow-x-auto p-4 lg:p-5">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-4 mt-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          {!isAllSelected && selectedTracks.length == 0 &&
            <>
              <Info size={24} className="text-theme_clr2" />
              <h1 className="text-white text-2xl font-semibold">Stem Splits</h1>
            </>
          }
          {tracks.length > 0 && (
            <button
              onClick={handleSelectAll}
              className="bg-theme_clr2 hover:bg-theme_clr4 text-white px-4 py-2 rounded-full flex items-center gap-2 transition-colors text-sm"
            >
              <span>{isAllSelected ? 'Deselect All' : 'Select All'}</span>
            </button>
          )}
          {selectedTracks.length > 0 ? (
            <>
              <button
                onClick={handleBulkDelete}
                disabled={isLoading}
                className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white px-3 py-2 rounded-full flex items-center gap-2 transition-colors text-sm"
              >
                <Trash2 size={16} />
                <span>Delete</span>
              </button>

              <button
                onClick={handleMoveToMultitrack}
                disabled={isMoving}
                className="bg-white text-theme_clr2 font-semibold px-3 py-2 rounded-full flex items-center gap-2 transition-colors text-sm"
              >
                <img src={MultitrackIcon} alt="Icon" />
                <span>{isMoving ? "Moving to Multitrack..." : "Move to Multitrack"}</span>
              </button>

              <div className="text-gray-300 text-sm ml-1">
                {selectedTracks.length} selected
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <span>Storage:</span>
              <span className="font-medium">{convertToMB(totalStorage)} MB used</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Search bar */}
          <div className="relative w-full md:w-[320px]">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by name"
              onChange={(e) => setQuery(e.target.value)}
              className="bg-transparent border border-gray-400 text-white rounded-full pl-12 pr-6 py-2.5 w-full"
            />
          </div>

          {/* Upload Button */}
          {/* <button onClick={addNew} className="bg-[#FA6138] hover:bg-[#ff724d] text-white px-6 py-2.5 rounded-full flex items-center gap-2 transition-colors">
            <Plus size={20} />
            <span>Upload</span>
          </button> */}

          {/* Settings Button */}
          {/* <Tooltip
            text={"Settings"}
            style={{ bg: "bg-white", text: "text-theme_clr1" }}
          >
            <button
              onClick={handleDialog}
              className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[40px] w-[40px]"
            >
              <img src={SettingsIcon} className="block" />
            </button>
          </Tooltip> */}
        </div>
      </header>
      {stemsLoading || isSearchLoading ?
        <MusicLoader /> :
        <div className="overflow-x-auto">
          <table className="w-full text-left border-separate border-spacing-y-2">
            <StemTrackTable
              allStems={tracks || []}
              key={selectedTracks}
              selectedTracks={selectedTracks}
              setSelectedTracks={setSelectedTracks}
            />
          </table>
        </div>
      }
      {/* Settings Dialog */}
      <Dialog title={"Settings"} hasActiveSubscription={hasActiveSubscription} isOpen={isDialogOpen} onClose={handleDialog} onSave={saveSettings} leftButton={'Cancel'} rightButton={'Save'}>
        <hr className="border-gray-700 mb-3" />
        {/* Mastering Preferences */}
        {!hasActiveSubscription &&
          <div className="mb-6">
            <h3 className="font-medium text-theme_clr2 mb-3">
              Mixing Preferences
            </h3>
            <div className="bg-theme_clr1 text-gray-300 p-4 rounded-xl flex flex-col gap-3">
              <p>
                <span className="mr-2">👑</span> Save your settings and speed up
                your workflow with a subscription.
              </p>
              <button className="bg-white text-[#0B1E3F] px-4 py-2 rounded-lg font-medium w-fit">
                Upgrade now
              </button>
            </div>
          </div>
        }
        <Settings
          genre={genre}
          setGenre={setGenre}
          loudness={loudness}
          setLoudness={setLoudness}
          sampleRate={sampleRate}
          setSampleRate={setSampleRate}
        />

        <hr className="border-gray-700" />
      </Dialog>
      <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/admin/urstudio/mix-enhancer"} />
    </div>
  );
};

export default StemSplitsLibrary;
